import React from 'react';
import { Banner, Button, ButtonClean, ButtonGroup, ConfirmModal, HeadContent, Skeleton, Subsection, useMediaQueryContext, useModalContext, } from '@fourthwall/components';
import { getPrice } from '@fourthwall/utils/dist/common';
import { useWebViewBridge } from '@fourthwall/webview-bridge';
import { isWebViewUserAgent } from '@fourthwall/webview-bridge/utils';
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString';
import { routing } from '@utils/routing';
import { AddSubscriptionModal, UpgradeSubscriptionBanner, getSubscriptionSamplesCreditGrant, useSubscription, useTopSubscriptionPlan, } from '../../../../modules/Subscriptions';
import * as S from './Samples.styled';
import { SamplesCreditBalance } from './components/SamplesCreditBalance/SamplesCreditBalance';
export const SamplesView = ({ credit, products, onOrderCreate }) => {
    const media = useMediaQueryContext();
    const { open } = useModalContext();
    const { undoCancelActiveSubscription, hasActiveProSubscription, pendingSubscription, activeSubscription, canUpgradeSubscription, } = useSubscription();
    const { topSubscriptionPlan } = useTopSubscriptionPlan();
    const { isWebView } = useWebViewBridge();
    const canReactivate = !pendingSubscription &&
        activeSubscription &&
        activeSubscription.scheduledCancelledAtTheEndOfPeriod;
    const getSamplesCreditAmount = () => {
        if (!topSubscriptionPlan)
            return '$10/month';
        const value = topSubscriptionPlan?.creditForSamples.value || 10;
        const period = topSubscriptionPlan?.recurringPeriod === 'YEARLY' ? 'year' : 'month';
        return `${getPrice(value, { currency: topSubscriptionPlan?.creditForSamples.currency })}/${period}`;
    };
    const handleUpgradeSubscription = () => {
        open(AddSubscriptionModal, {
            title: `Upgrade to Pro and receive ${getSamplesCreditAmount()} in sample credit`,
            source: 'settings_samples',
        });
    };
    const renderUpgradeSubscriptionBanner = () => {
        if (isWebView)
            return null;
        if (canReactivate || !canUpgradeSubscription)
            return null;
        return (React.createElement(S.Container, null,
            React.createElement(UpgradeSubscriptionBanner, { source: "settings_samples", onClick: handleUpgradeSubscription },
                "Upgrade to ",
                React.createElement("strong", null, "Pro"),
                " and receive ",
                getSamplesCreditAmount(),
                " in sample credit.")));
    };
    const openUndoCancelSubscriptionConfirmModal = () => {
        const title = activeSubscription?.activeTo ? (React.createElement(React.Fragment, null,
            "You will automatically renew Pro plan on",
            ' ',
            React.createElement(TimezonedDateString, { date: activeSubscription.activeTo, format: "DD MMM, YYYY" }))) : null;
        open(ConfirmModal, {
            title: 'Your subscription is scheduled to be canceled. Would you like to undo this?',
            text: title,
            cancelLabel: 'Keep canceled',
            confirmLabel: 'Renew subscription',
            onConfirm: async () => {
                await undoCancelActiveSubscription();
            },
        });
    };
    const renderReactivateSubscriptionBanner = () => {
        if (isWebView)
            return null;
        if (!canReactivate || !activeSubscription || !activeSubscription.activeTo)
            return null;
        return (React.createElement(S.Container, null,
            React.createElement(UpgradeSubscriptionBanner, { source: "settings_samples", buttonLabel: "Reactivate", onClick: openUndoCancelSubscriptionConfirmModal },
                "Get",
                ' ',
                getSubscriptionSamplesCreditGrant({
                    subscriptionPlan: activeSubscription.plan,
                    withPeriod: false,
                }),
                ' ',
                "in free credit every",
                ' ',
                activeSubscription.plan.recurringPeriod === 'YEARLY' ? 'year' : 'month',
                " with",
                ' ',
                React.createElement("strong", null, "Fourthwall Pro"),
                "! Reactivate your subscription by",
                ' ',
                React.createElement(TimezonedDateString, { date: activeSubscription.activeTo, format: "DD MMM, YYYY" }),
                " to claim your bonus.")));
    };
    const renderSubscriptionGrantBanner = () => {
        if (isWebView)
            return null;
        if (!hasActiveProSubscription ||
            !activeSubscription ||
            !activeSubscription.activeTo ||
            !activeSubscription?.plan.creditForSamples.value ||
            canReactivate) {
            return null;
        }
        if (!pendingSubscription &&
            activeSubscription &&
            activeSubscription.scheduledCancelledAtTheEndOfPeriod) {
            return (React.createElement(S.Container, null,
                React.createElement(Banner, { appearance: "pro" },
                    "You would receive",
                    ' ',
                    getSubscriptionSamplesCreditGrant({
                        subscriptionPlan: activeSubscription?.plan,
                    }),
                    ' ',
                    "of free sample credit from ",
                    React.createElement("strong", null, "Fourthwall Pro"),
                    ".",
                    ' ',
                    React.createElement(ButtonClean, { appearance: "inherit", label: "Reactivate subscription", onClick: openUndoCancelSubscriptionConfirmModal }),
                    ' ',
                    "to receive",
                    ' ',
                    getSubscriptionSamplesCreditGrant({
                        subscriptionPlan: activeSubscription?.plan,
                        withPeriod: false,
                    }),
                    ' ',
                    "on ",
                    React.createElement(TimezonedDateString, { date: activeSubscription.activeTo, format: "DD MMM, YYYY" }),
                    ".",
                    ' ')));
        }
        return (React.createElement(S.Container, null,
            React.createElement(Banner, { appearance: "pro" },
                "You're receiving",
                ' ',
                getSubscriptionSamplesCreditGrant({
                    subscriptionPlan: activeSubscription?.plan,
                }),
                ' ',
                "of free sample credit from ",
                React.createElement("strong", null, "Fourthwall Pro"),
                ".",
                ' ',
                getSubscriptionSamplesCreditGrant({
                    subscriptionPlan: activeSubscription?.plan,
                    withPeriod: false,
                }),
                ' ',
                "will be added on",
                ' ',
                React.createElement(TimezonedDateString, { date: activeSubscription.activeTo, format: "DD MMM, YYYY" }))));
    };
    const renderProducts = () => {
        if (products.status === 'HIDDEN') {
            return null;
        }
        if (products.status === 'LOADING') {
            return (React.createElement("div", { style: { marginTop: 16 } },
                React.createElement(Skeleton, null,
                    React.createElement(Skeleton.Shape, { width: media.tablet ? 180 : '100%', height: 48 }))));
        }
        if (products.status === 'NO_PRODUCTS') {
            return (React.createElement("div", { style: { marginTop: 16 } },
                React.createElement("div", { style: { marginBottom: 16 } }, "You don't have any products that you can order samples for (only products without minimums are currently able to be ordered as samples)"),
                React.createElement(Button, { appearance: "primary", label: "Manage your products", onClick: products.onManage })));
        }
        if (products.status === 'AVAILABLE') {
            return (React.createElement("div", { style: { marginTop: 16 } },
                React.createElement(ButtonGroup, { fullWidth: !media.tablet },
                    React.createElement(Button, { label: "Create a new order", onClick: onOrderCreate }))));
        }
    };
    return (React.createElement(Subsection, { title: isWebViewUserAgent(navigator.userAgent) ? '' : 'Samples' },
        React.createElement(HeadContent, { title: "Samples \u2013 Settings" }),
        renderUpgradeSubscriptionBanner(),
        renderReactivateSubscriptionBanner(),
        renderSubscriptionGrantBanner(),
        React.createElement("p", null,
            "Order pre-production samples of your products for quality checks and promotion. You can use sample credit (if available) or a credit card to pay for your samples.",
            ' ',
            React.createElement(ButtonClean, { label: "Learn more", href: routing.external.helpCenter.orderingSamples, target: "_blank" })),
        React.createElement("p", { style: { marginTop: 12, fontWeight: 600 } }, credit ? (React.createElement(SamplesCreditBalance, { label: "Your current samples credit", balance: credit })) : (React.createElement(Skeleton, null,
            React.createElement(Skeleton.Shape, { width: 200 })))),
        renderProducts()));
};
